import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LayerItem, BuildingColorMode, ProposalVariant } from './types';
import { MOCK_LAYERS } from './mocks';

// ─── Data fetching ────────────────────────────────────────────────────────────

export const QUERY_KEYS = {
  layers: ['urban-map', 'layers'] as const,
};

function useLayersQuery() {
  return useQuery<LayerItem[]>({
    queryKey: QUERY_KEYS.layers,
    queryFn: async () => {
      const res = await fetch('/api/urban-map/layers');
      if (!res.ok) {
        throw new Error('Impossible de récupérer le catalogue des couches.');
      }
      return res.json() as Promise<LayerItem[]>;
    },
    initialData: MOCK_LAYERS,
  });
}

// ─── Layer catalog state (visibility, favorites, search) ────────────────────

export function useLayersCatalog() {
  const { data: initialLayers, isLoading, isError } = useLayersQuery();
  const [layers, setLayers] = useState<LayerItem[]>(initialLayers ?? MOCK_LAYERS);
  const [search, setSearch] = useState('');

  const toggleVisibility = (id: string) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)));
  };

  const toggleFavorite = (id: string) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, isFavorite: !l.isFavorite } : l)));
  };

  const filteredLayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return layers;
    return layers.filter((l) => l.label.toLowerCase().includes(query));
  }, [layers, search]);

  const favoriteLayers = useMemo(() => layers.filter((l) => l.isFavorite), [layers]);

  const visibleCount = useMemo(() => layers.filter((l) => l.visible).length, [layers]);

  return {
    layers,
    filteredLayers,
    favoriteLayers,
    visibleCount,
    search,
    setSearch,
    toggleVisibility,
    toggleFavorite,
    isLoading,
    isError,
  };
}

/** Trie une liste de couches en mettant les données internes ENGIE en premier. */
export function sortEngieFirst(layerList: LayerItem[]): LayerItem[] {
  return [...layerList].sort((a, b) => Number(b.isEngieInternal) - Number(a.isEngieInternal));
}

// ─── Building layer color mode ───────────────────────────────────────────────

export function useBuildingColorMode() {
  const [mode, setMode] = useState<BuildingColorMode>('heat');
  return { mode, setMode };
}

// ─── Proposal variant switcher (A = onglets, B = accordéon) ─────────────────

export function useProposalVariant() {
  const [variant, setVariant] = useState<ProposalVariant>('tabs');
  return { variant, setVariant };
}
